import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HRISConnector = ({ connector, onConnect, onDisconnect, onSync }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    await onConnect(connector?.id);
    setIsConnecting(false);
  };

  const handleSync = async () => {
    setIsSyncing(true);
    await onSync(connector?.id);
    setIsSyncing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'error': return 'text-destructive';
      case 'syncing': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'error': return 'XCircle';
      case 'syncing': return 'RefreshCw';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <Icon name={connector?.icon} size={24} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{connector?.name}</h3>
            <p className="text-sm text-muted-foreground">{connector?.description}</p>
          </div>
        </div>
        <div className={`flex items-center space-x-2 ${getStatusColor(connector?.status)}`}>
          <Icon 
            name={getStatusIcon(connector?.status)} 
            size={16} 
            className={connector?.status === 'syncing' ? 'animate-spin' : ''} 
          />
          <span className="text-sm font-medium capitalize">{connector?.status}</span>
        </div>
      </div>
      {connector?.status === 'connected' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-muted rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">{t('dataIntegration.hris.lastSync')}</p>
            <p className="text-sm font-medium text-foreground">{connector?.lastSync}</p>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">{t('dataIntegration.hris.recordsSynced')}</p>
            <p className="text-sm font-medium text-foreground">{connector?.recordCount?.toLocaleString()}</p>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">{t('dataIntegration.hris.dataQuality')}</p>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-background rounded-full h-2">
                <div 
                  className="bg-success h-2 rounded-full" 
                  style={{ width: `${connector?.dataQuality}%` }}
                />
              </div>
              <span className="text-sm font-medium text-foreground">{connector?.dataQuality}%</span>
            </div>
          </div>
        </div>
      )}
      {connector?.status === 'error' && connector?.errorMessage && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-destructive mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">{t('dataIntegration.hris.connectionError')}</p>
              <p className="text-sm text-destructive/80 mt-1">{connector?.errorMessage}</p>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center space-x-3">
        {connector?.status === 'disconnected' ? (
          <Button
            variant="default"
            loading={isConnecting}
            iconName="Link"
            iconPosition="left"
            onClick={handleConnect}
          >
            {t('dataIntegration.hris.connect')}
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              loading={isSyncing}
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleSync}
              disabled={connector?.status === 'error'}
            >
              {t('dataIntegration.hris.syncNow')}
            </Button>
            <Button
              variant="ghost"
              iconName="Settings"
              iconPosition="left"
              onClick={() => navigate('/admin/console')}
            >
              {t('dataIntegration.hris.configure')}
            </Button>
            <Button
              variant="destructive"
              iconName="Unlink"
              iconPosition="left"
              onClick={() => onDisconnect(connector?.id)}
            >
              {t('dataIntegration.hris.disconnect')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default HRISConnector;